// frontend/src/components/Filters/TimeframeSelect.tsx
// One searchable, grouped timeframe selector: live presets + fixed frames + rolling frames + campaigns.
// Single-select. Presets drive setPreset; absolute frames/campaigns set from/to + tf_preset=custom.
import { Autocomplete, Box, TextField } from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined'
import type { TimeframePresetKey } from '~/state/useTimeframePresetState'
import type { CampaignTimeslotItem } from '~/api/endpoints/getControls'
import { predicateOf } from '~/state/campaignPredicate'
import {
  CONTROLS,
  TIMEFRAME_PRESET_LABELS,
  TIMEFRAME_GROUPS,
} from '~/components/Charts/chrome/chartChrome'

export interface AbsoluteTimeframe {
  value: string
  label: string
  from: string
  to: string
}

/** controls items -> absolute options (keeps only items with both bounds). Shared by the
 *  scaffold's selector wiring and useTimeframeLabel (one filter rule, two consumers). */
export function toAbsoluteTimeframes(
  items?: Array<{ value: string; label: string; from?: string; to?: string }>,
): AbsoluteTimeframe[] {
  return (items ?? [])
    .filter((i) => i.from && i.to)
    .map((i) => ({ value: i.value, label: i.label, from: i.from!, to: i.to! }))
}

/** Rolling DB frames surfaced in the selector (operator-curated, WP-C): 83=Last 13 Months,
 *  84=Last 3 FYs, 85=Last 26 Months, 87=Last CY. Dropped: FY YTD/CY YTD/Last 4 Weeks (a live preset
 *  covers them) + Last 6 Weeks (editorial). Curation lives FE-side because the de-dup is coupled to
 *  the FE presets; get_controls stays preset-agnostic. Follow-up filed: a backend surface_in_selector
 *  flag if operator-controlled curation is ever wanted. Keyed by the DB campaign_id column (text) — the shared id for all timeframe rows, rolling included. */
export const SURFACED_ROLLING_FRAME_IDS: ReadonlySet<string> = new Set(['83', '84', '85', '87'])

/** controls timeframe_rolling items -> the curated, surfaced absolute frames. Shared by the scaffold
 *  (selector prop) and useTimeframeLabel (label round-trip) — one rule, two consumers. */
export function toSurfacedRollingTimeframes(
  items?: Array<{ value: string; label: string; from?: string; to?: string }>,
): AbsoluteTimeframe[] {
  return toAbsoluteTimeframes(items).filter((t) => SURFACED_ROLLING_FRAME_IDS.has(t.value))
}

type TimeframeOption =
  | { kind: 'preset'; group: string; value: TimeframePresetKey; label: string }
  | { kind: 'absolute'; group: string; value: string; label: string; from: string; to: string }
  | {
      kind: 'campaign'
      group: string
      value: string
      label: string
      from: string
      to: string
      item: CampaignTimeslotItem
    }

export const PRESET_OPTIONS: Array<{ value: TimeframePresetKey; label: string }> = [
  { value: 'last_4_weeks', label: TIMEFRAME_PRESET_LABELS.last_4_weeks! },
  { value: 'last_12_weeks', label: TIMEFRAME_PRESET_LABELS.last_12_weeks! },
  { value: 'last_quarter', label: TIMEFRAME_PRESET_LABELS.last_quarter! },
  { value: 'ytd', label: TIMEFRAME_PRESET_LABELS.ytd! },
  { value: 'fy_ytd', label: TIMEFRAME_PRESET_LABELS.fy_ytd! },
  { value: 'last_year', label: TIMEFRAME_PRESET_LABELS.last_year! },
  { value: 'all_time', label: TIMEFRAME_PRESET_LABELS.all_time! },
  { value: 'custom', label: TIMEFRAME_PRESET_LABELS.custom! },
]

interface Props {
  preset: TimeframePresetKey
  fromIso: string
  toIso: string
  frames: AbsoluteTimeframe[]
  rolling: AbsoluteTimeframe[]
  campaignItems: CampaignTimeslotItem[]
  onSelectPreset: (preset: TimeframePresetKey) => void
  onSelectAbsolute: (from: string, to: string) => void
  onSelectCampaign: (item: CampaignTimeslotItem) => void
  /** FU-07: the ACTIVE campaign's id. When set (preset=custom), the displayed value matches by
   *  campaign IDENTITY first — bounds-matching is ambiguous across identical-window siblings
   *  (the first-match rule showed the wrong sibling's label; data/chips/captions were already
   *  identity-correct via useTimeframeLabel). Bounds stays the fallback (frames/rolling,
   *  stale id). */
  activeCampaignId?: string | null
}

export function TimeframeSelect({
  preset,
  fromIso,
  toIso,
  frames,
  rolling,
  campaignItems,
  onSelectPreset,
  onSelectAbsolute,
  onSelectCampaign,
  activeCampaignId,
}: Props) {
  // Pre-ordered so the Autocomplete groups render Presets → Fixed Frames → Rolling Frames → Campaigns.
  const options: TimeframeOption[] = [
    ...PRESET_OPTIONS.map(
      (p): TimeframeOption => ({
        kind: 'preset',
        group: TIMEFRAME_GROUPS.presets,
        value: p.value,
        label: p.label,
      }),
    ),
    ...frames.map(
      (f): TimeframeOption => ({ kind: 'absolute', group: TIMEFRAME_GROUPS.fixed, ...f }),
    ),
    ...rolling.map(
      (r): TimeframeOption => ({ kind: 'absolute', group: TIMEFRAME_GROUPS.rolling, ...r }),
    ),
    ...campaignItems
      .filter((i) => i.from && i.to)
      .map(
        (i): TimeframeOption => ({
          kind: 'campaign',
          group: TIMEFRAME_GROUPS.campaigns,
          value: i.value,
          label: i.label,
          from: i.from!,
          to: i.to!,
          item: i,
        }),
      ),
  ]

  // Selected value: a preset when not custom; else the bounded option (fixed/rolling/campaign)
  // whose bounds match the current window (round-trips a campaign/frame pick across reloads),
  // else the Custom preset. For campaigns: identity matches by activeCampaignId first (to avoid
  // ambiguity with identical-window siblings where first-match would show the wrong sibling's label;
  // data/chips/captions were already identity-correct via useTimeframeLabel). Bounds-matching is
  // the fallback (frames, rolling, stale id). On a from/to collision the first match wins.
  const matchedOption: TimeframeOption | null =
    preset !== 'custom'
      ? (options.find((o) => o.kind === 'preset' && o.value === preset) ?? null)
      : ((activeCampaignId
          ? options.find((o) => o.kind === 'campaign' && o.value === activeCampaignId)
          : undefined) ??
        options.find((o) => o.kind !== 'preset' && o.from === fromIso && o.to === toIso) ??
        options.find((o) => o.kind === 'preset' && o.value === 'custom') ??
        null)
  // Debris-wave Fix B: clone before handing to Autocomplete as `value`. MUI's useAutocomplete
  // short-circuits onChange on `value === newValue` (plain reference equality — see
  // handleValue), and `matchedOption` is `.find()`'d out of the SAME `options` array the clicked
  // list item comes from, so without cloning they're the identical object and re-selecting an
  // already-bounds-matched option is a silent no-op. That bit a real flow: deleting a campaign's
  // managed-dim chip clears the campaign id but leaves from/to at the campaign's window, so the
  // option still LOOKS selected; re-picking it must still re-fire onSelectCampaign to re-apply
  // the full predicate. isOptionEqualToValue (kind+value) still matches the clone, so the
  // listbox highlight and the input's displayed label are unaffected.
  const selected: TimeframeOption | null = matchedOption ? { ...matchedOption } : null

  return (
    <Autocomplete
      sx={{ minWidth: 240 }}
      options={options}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.kind === v.kind && o.value === v.value}
      value={selected ?? undefined}
      disableClearable
      onChange={(_, opt) => {
        if (!opt) return
        if (opt.kind === 'preset') onSelectPreset(opt.value)
        else if (opt.kind === 'campaign') onSelectCampaign(opt.item)
        else onSelectAbsolute(opt.from, opt.to)
      }}
      renderOption={(props, option) => {
        // FU-08: every campaign option carries a badge — predicate campaigns keep the filter
        // icon; window-only campaigns get a distinct muted window icon (a window-only campaign
        // on a carrying page filters by identity, so badge-free read as "inert", which it isn't).
        const badge =
          option.kind !== 'campaign' ? null : Object.keys(predicateOf(option.item)).length > 0 ? (
            <FilterAltOutlinedIcon
              data-testid="campaign-predicate-badge"
              fontSize="inherit"
              aria-label={CONTROLS.campaignHasFilters}
              sx={{ ml: 0.5 }}
            />
          ) : (
            <DateRangeOutlinedIcon
              data-testid="campaign-window-badge"
              fontSize="inherit"
              aria-label={CONTROLS.campaignWindowOnly}
              sx={{ ml: 0.5, color: 'text.disabled' }}
            />
          )
        return (
          <Box component="li" {...props} key={`${option.kind}-${option.value}`}>
            {option.label}
            {badge}
          </Box>
        )
      }}
      renderInput={(params) => <TextField {...params} label={CONTROLS.timeframe} />}
    />
  )
}
